import { type MouseEvent, memo, useCallback, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { MinusIcon, PlusIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Slider } from '@air/primitive-slider';
import { Tooltip } from '@air/primitive-tooltip';
import { PERCENT_SCALE } from '@air/utilities';

import { MinimapIcon } from '~/components/AssetModalV2/Content/Footer/components/MinimapIcon';

const ZOOM_TOOLTIP_HIDE_DELAY_MS = 1500;

export type ZoomSliderProps = {
  onZoomIn: () => void;
  onZoomOut: () => void;
  zoom: number;
  onSliderChange?: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  isMinimapOpen?: boolean;
  minimapControlsId?: string;
  minimapToggleId?: string;
  onToggleMinimap?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export const ZoomSlider = memo(
  ({
    isMinimapOpen = false,
    max = 3,
    min = 0.5,
    minimapControlsId,
    minimapToggleId,
    onSliderChange,
    onToggleMinimap,
    onZoomIn,
    onZoomOut,
    step = 0.1,
    zoom,
  }: ZoomSliderProps) => {
    const [showTooltip, setShowTooltip] = useState<boolean | undefined>(undefined);

    const [hideTooltip] = useDebouncedCallback(() => {
      setShowTooltip(undefined);
    }, ZOOM_TOOLTIP_HIDE_DELAY_MS);

    const formatZoomTooltip = useCallback((values: number[]) => `${Math.round(values[0] * PERCENT_SCALE)}%`, []);

    const handleValueChange = useCallback(
      (value: number[]) => {
        if (onSliderChange) {
          onSliderChange(value);
        }
      },
      [onSliderChange],
    );

    const handleZoomIn = useCallback(() => {
      onZoomIn();
      setShowTooltip(true);
      // After showing the tooltip, to prevent it from being stuck on the screen. We call hideTooltip which fires after 1.5s after the user has stopped interacting with the event
      hideTooltip(); // Reset the debounced timer
    }, [onZoomIn, hideTooltip]);

    const handleZoomOut = useCallback(() => {
      onZoomOut();
      setShowTooltip(true);
      hideTooltip(); // Reset the debounced timer
    }, [onZoomOut, hideTooltip]);

    const minimapTooltipLabel = isMinimapOpen ? 'Hide minimap' : 'Show minimap';

    return (
      <div
        className="flex items-center gap-2"
        data-image-minimap-controls={onToggleMinimap ? '' : undefined}
        data-testid="ZOOM_SLIDER"
      >
        {onToggleMinimap ? (
          <Tooltip label={minimapTooltipLabel}>
            <IconButton
              appearance="ghost"
              aria-controls={minimapControlsId}
              aria-pressed={isMinimapOpen}
              color={isMinimapOpen ? 'blue' : 'gray'}
              data-minimap-toggle=""
              data-testid="MINIMAP_TOGGLE_BUTTON"
              icon={MinimapIcon}
              id={minimapToggleId}
              label="Minimap"
              onClick={onToggleMinimap}
              size="medium"
            />
          </Tooltip>
        ) : null}
        <div className="flex w-[160px] items-center gap-2">
          <IconButton
            adsIcon
            onClick={handleZoomOut}
            icon={MinusIcon}
            label="Zoom out"
            appearance="ghost"
            color="gray"
            size="medium"
            data-testid="ZOOM_OUT_BUTTON"
          />
          <Slider
            defaultValue={[zoom]}
            formatTooltip={formatZoomTooltip}
            tooltipPosition="top"
            tooltipDisplayStrategy="on-drag"
            showTooltip={showTooltip}
            value={[zoom]}
            max={max}
            min={min}
            step={step}
            rangeColor="grey"
            onValueChange={handleValueChange}
          />
          <IconButton
            adsIcon
            onClick={handleZoomIn}
            icon={PlusIcon}
            label="Zoom in"
            appearance="ghost"
            color="gray"
            size="medium"
            data-testid="ZOOM_IN_BUTTON"
          />
        </div>
      </div>
    );
  },
);

ZoomSlider.displayName = 'ZoomSlider';
